import { Link } from "react-router-dom";
import { ArrowRight, Route as RouteIcon, Info, CalendarDays, ShieldAlert } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import DestinationCard from "@/components/DestinationCard";
import { useEnquiry } from "@/components/Enquiry";
import { CHAR_DHAM, DESTINATION_BY_SLUG } from "@/data/destinations";
import { IMG, img } from "@/data/images";

const NIGHT_HALTS = ["Barkot (for Yamunotri)", "Uttarkashi (for Gangotri)", "Guptkashi / Phata (for Kedarnath)", "Joshimath or Pipalkoti (for Badrinath)"];

export default function CharDham() {
  const { openEnquiry } = useEnquiry();
  return (
    <>
      <Seo
        title="Uttarakhand Char Dham Taxi Service — Yamunotri, Gangotri, Kedarnath, Badrinath | Tingadya Travels"
        description="Plan the Char Dham Yatra with Tingadya Travels — route planning, night halts, trek bases and multi-day Ertiga taxi enquiries from Haridwar, Dehradun or Rishikesh."
      />
      <section className="relative flex min-h-[62vh] items-end overflow-hidden sm:min-h-[70vh]" data-testid="chardham-hero">
        <img src={img(IMG.chardham_banner[0], 1800)} alt="Himalayan temple above a Garhwal valley — the Char Dham Yatra landscape" className="absolute inset-0 h-full w-full object-cover" fetchPriority="high" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/50 to-ink/20" />
        <div className="container-x relative z-10 pb-14 pt-32">
          <p className="eyebrow">The Sacred Circuit</p>
          <h1 className="mt-3 max-w-3xl font-display text-4xl font-bold text-white sm:text-5xl lg:text-6xl" data-testid="chardham-title">Char Dham Yatra, Planned Like Locals</h1>
          <p className="mt-4 max-w-2xl leading-relaxed text-snow/85">
            Four shrines, one journey — Yamunotri, Gangotri, Kedarnath and Badrinath, paced across mountain days with the right night halts, trek bases and season windows.
          </p>
          <button onClick={() => openEnquiry("Char Dham Yatra", "Char Dham page", { tripDays: "Customised trip" })} data-testid="chardham-plan-my-yatra-button" className="btn-gold mt-8">
            Plan My Yatra <ArrowRight size={16} />
          </button>
        </div>
      </section>

      {/* Four dhams */}
      <section className="section-pad" data-testid="chardham-dhams">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">The Four Dhams</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">West to East, or Your Own Order</h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {CHAR_DHAM.map((d, i) => (
              <Reveal key={d.slug} delay={i * 0.07}>
                <DestinationCard d={d} testid={`chardham-card-${d.slug}`} />
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Route planning */}
      <section className="border-t border-white/5 bg-coal/50 py-16 sm:py-24" data-testid="chardham-route-planning">
        <div className="container-x grid gap-10 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow">Route Planning</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">How the Yatra Usually Flows</h2>
            <p className="mt-4 leading-relaxed text-mist">
              Most pilgrims travel the traditional west-to-east order — Yamunotri first, then Gangotri, Kedarnath and finally Badrinath — starting from Haridwar, Dehradun or Rishikesh. The circuit is typically planned over multiple days at a pilgrim's pace, and the shape of your plan changes with weather, darshan timings and the opening calendar of each shrine.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
              {NIGHT_HALTS.map((h, i) => (
                <div key={h} className="flex items-center gap-4 rounded-2xl border border-white/10 bg-ink/60 p-4" data-testid={`chardham-night-halt-${i + 1}`}>
                  <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-gold/50 font-mono text-xs font-bold text-gold">{i + 1}</span>
                  <p className="text-sm font-medium text-snow">{h}</p>
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-start gap-3 rounded-2xl border border-gold/30 bg-gold/10 p-5">
              <Info size={18} className="mt-0.5 shrink-0 text-gold" />
              <p className="text-sm leading-relaxed text-snow/90">
                Night halts and day counts are indicative, not fixed — we rebuild the route around your dates, pace and the current road and weather conditions.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="flex flex-col gap-6">
              <div className="rounded-3xl border border-white/10 bg-ink p-7" data-testid="chardham-registration-card">
                <h3 className="flex items-center gap-2 font-display text-xl font-bold text-gold"><ShieldAlert size={18} /> Registration & Yatra Rules</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">
                  Pilgrims must register on the official Uttarakhand Char Dham yatra portal for Kedarnath, Badrinath, Gangotri and Yamunotri. The Yamunotri and Kedarnath shrines are reached by trek from the road head (pony and palki services are available locally). Temple opening dates change every year — typically closing through the winter months — so we always confirm the current calendar before your dates are fixed.
                </p>
              </div>
              <div className="rounded-3xl border border-white/10 bg-ink p-7" data-testid="chardham-taxi-card">
                <h3 className="flex items-center gap-2 font-display text-xl font-bold text-gold"><RouteIcon size={18} /> Multi-Day Taxi, One Crew</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">
                  The whole circuit runs in our Maruti Suzuki Ertiga VXI with a hill-experienced driver who doubles as your route planner — night halts, darshan timing tips, and pickups after every trek. Tell us your days and pilgrims; we build the plan.
                </p>
                <button onClick={() => openEnquiry("Char Dham Yatra — Multi-day Taxi", "Char Dham page taxi card", { tripDays: "Customised trip" })} data-testid="chardham-taxi-enquiry-button" className="btn-gold mt-6 w-full">
                  Request Multi-Day Yatra Taxi
                </button>
              </div>
              <div className="rounded-3xl border border-gold/30 bg-gradient-to-br from-gold/15 to-transparent p-7" data-testid="chardham-season-card">
                <h3 className="flex items-center gap-2 font-display text-xl font-bold text-white"><CalendarDays size={18} /> Best Yatra Windows</h3>
                <p className="mt-3 text-sm leading-relaxed text-mist">
                  The shrines open after winter, usually around late April to May, and close around Diwali. May–June and September–October are the classic windows; monsoon weeks need flexible plans. We reconfirm dates, weather and road status with you before departure.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Extended sacred trail */}
      <section className="section-pad" data-testid="chardham-extended">
        <div className="container-x">
          <Reveal>
            <p className="eyebrow">Beyond the Four</p>
            <h2 className="mt-3 font-display text-3xl font-bold text-white sm:text-4xl">Extend the Journey</h2>
            <p className="mt-3 max-w-2xl text-mist">Add these sacred stops along the way — each opens on its own page.</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {["hemkund-sahib", "triyuginarayan", "dhari-devi", "tungnath", "neelkanth-mahadev", "har-ki-pauri", "mansa-devi", "chandi-devi"].map((s, i) => {
              const d = DESTINATION_BY_SLUG[s];
              return d ? <Reveal key={s} delay={(i % 4) * 0.06}><DestinationCard d={d} testid={`chardham-extended-${s}`} /></Reveal> : null;
            })}
          </div>
          <div className="mt-10 text-center">
            <Link to="/explore" data-testid="chardham-explore-all-button" className="btn-ghost-gold">Explore All Destinations</Link>
          </div>
        </div>
      </section>
    </>
  );
}
